import toast from "react-hot-toast";

import { AppDialog } from "../../../components/ui";
import { PointRule, getPointRuleDisplayName } from "../../../models/pointRule";
import { useDeactivatePointRuleMutation } from "../../../store/services/point-rules";
import { getApiErrorMessage } from "../../../utils/functions/api-errors";

export interface DeactivatePointRuleDialogProps {
  /** Regla a dar de baja. null = modal cerrado. */
  rule: PointRule | null;
  onHide: () => void;
}

export const DEACTIVATE_POINT_RULE_SUCCESS_TOAST =
  "Regla dada de baja. Los puntos ya acreditados no cambian.";
const DEACTIVATE_FALLBACK_MESSAGE = "No se pudo dar de baja la regla";

/**
 * Baja lógica de una regla de puntos. Las tres cosas que hay que decir antes de confirmar, sin
 * vueltas: deja de pagar, lo ya acreditado no se devuelve, y se puede volver a crear igual.
 *
 * Una regla de inasistencia descuenta en vez de dar, así que las dos primeras se dicen al
 * revés: "no se devuelven" sobre puntos que nunca se sumaron no se entiende.
 */
export const DeactivatePointRuleDialog = ({ rule, onHide }: DeactivatePointRuleDialogProps) => {
  const [deactivatePointRule, { isLoading }] = useDeactivatePointRuleMutation();

  const handleConfirm = async () => {
    if (!rule) return;
    try {
      await deactivatePointRule({ id: rule.id, tenantId: rule.tenantId }).unwrap();
      toast.success(DEACTIVATE_POINT_RULE_SUCCESS_TOAST);
      onHide();
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? DEACTIVATE_FALLBACK_MESSAGE);
    }
  };

  return (
    <AppDialog
      variant="destructive"
      visible={rule !== null}
      onHide={onHide}
      header="Dar de baja la regla"
      primaryLabel="Dar de baja la regla"
      onPrimary={() => void handleConfirm()}
      loading={isLoading}
    >
      {rule && (
        <>
          <p className="m-0">
            {rule.isPenalty
              ? `"${getPointRuleDisplayName(rule)}" deja de descontar puntos y sale del listado.`
              : `"${getPointRuleDisplayName(rule)}" deja de dar puntos y sale del listado.`}
          </p>
          <p className="m-0 mt-3">
            {rule.isPenalty
              ? "Los puntos que ya descontó no se devuelven: el saldo y el historial de cada usuario quedan como están."
              : "Los puntos ya acreditados no se devuelven: siguen en el saldo y en el historial de cada usuario."}
          </p>
          <p className="m-0 mt-3">Si la necesitás de nuevo, después podés crear otra regla igual.</p>
        </>
      )}
    </AppDialog>
  );
};
